import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Reusable "Change Password" card.
 * Drop into any dashboard (Member, Influencer, Admin) — the accent color adapts.
 *
 * Props:
 *   accent = "cyan" | "magenta"   → color of headings + focus ring + CTA
 *   showTempKeyBanner = boolean   → true to add the "temp key nudge" strip on top
 */
export default function ChangePasswordCard({ accent = "cyan", showTempKeyBanner = false }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const accentClasses =
    accent === "magenta"
      ? {
          border: "border-fuchsia-400/20",
          eyebrow: "text-fuchsia-400/80",
          focus: "focus:border-fuchsia-400/70",
          cta: "bg-fuchsia-500 text-black",
        }
      : {
          border: "border-cyan-400/20",
          eyebrow: "text-cyan-400/80",
          focus: "focus:border-cyan-400/70",
          cta: "bg-white text-black",
        };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    if (next === current) {
      toast.error("New password must differ from the current one.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Your key has been rewritten.");
      setCurrent(""); setNext(""); setConfirm("");
      setOpen(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not change password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={`mt-12 glass rounded-sm p-6 border ${accentClasses.border}`} data-testid="change-password-card">
      {showTempKeyBanner && !open && (
        <div className="mb-5 px-4 py-3 border border-fuchsia-400/30 bg-fuchsia-400/5 rounded-sm" data-testid="temp-key-nudge">
          <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-fuchsia-400/80">— reminder —</div>
          <div className="font-mono text-xs text-white/80 mt-1">
            You signed in with a temporary key. Rewrite it below to something only you know.
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className={`font-mono text-[10px] tracking-[0.4em] uppercase ${accentClasses.eyebrow}`}>— your key —</div>
          <h2 className="font-display text-xl font-black uppercase mt-1">Change password</h2>
          <p className="font-mono text-[11px] text-white/50 mt-1">
            Rewrite the key that opens your account. Minimum 8 characters.
          </p>
        </div>
        {!open && (
          <button
            onClick={() => setOpen(true)}
            data-testid="change-password-toggle"
            className={`px-5 py-2 border border-white/20 text-white font-display font-bold uppercase tracking-[0.3em] text-[11px] rounded-sm hover:bg-white/5 transition`}
          >
            Rewrite Key
          </button>
        )}
      </div>

      {open && (
        <form onSubmit={submit} className="mt-6 space-y-3 max-w-md" data-testid="change-password-form">
          <input
            type="password"
            required
            autoComplete="current-password"
            placeholder="Current password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            data-testid="change-password-current"
            className={`w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none ${accentClasses.focus}`}
          />
          <input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            placeholder="New password (min 8)"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            data-testid="change-password-new"
            className={`w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none ${accentClasses.focus}`}
          />
          <input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            placeholder="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="change-password-confirm"
            className={`w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none ${accentClasses.focus}`}
          />
          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={saving}
              data-testid="change-password-submit"
              className={`gate px-5 py-2 ${accentClasses.cta} font-display font-bold uppercase tracking-[0.3em] text-[11px] rounded-sm hover:scale-[1.02] transition disabled:opacity-50`}
            >
              {saving ? "..." : "Save New Key"}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setCurrent(""); setNext(""); setConfirm(""); }}
              disabled={saving}
              data-testid="change-password-cancel"
              className="px-5 py-2 border border-white/20 text-white/70 font-display font-bold uppercase tracking-[0.3em] text-[11px] rounded-sm hover:bg-white/5"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
